import { create } from 'zustand';
import api from '../services/api';

export type UserType = 'individual' | 'freelancer' | 'small_company';

export type SearchCategory = 'medical' | 'real_estate' | 'restaurant' | 'legal' | 'automotive' | 'home_services' | 'other';

export interface UserMe {
  id: number;
  email: string;
  full_name: string | null;
  user_type: UserType | null;
  job_title: string | null;
  company_name: string | null;
  company_website: string | null;
  team_size: number | null;
  search_country: string | null;
  search_category: SearchCategory | null;
}

interface AuthState {
  isAuthenticated: boolean;
  user: UserMe | null;
  meLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, fullName: string) => Promise<void>;
  logout: () => void;
  setAuthenticated: (token: string) => void;
  fetchMe: () => Promise<UserMe>;
  updateMe: (payload: Partial<Pick<UserMe, 'full_name' | 'user_type' | 'job_title' | 'company_name' | 'company_website' | 'team_size' | 'search_country' | 'search_category'>>) => Promise<UserMe>;
}

export const useAuthStore = create<AuthState>((set) => ({
  isAuthenticated: !!localStorage.getItem('access_token'),
  user: null,
  meLoading: false,

  login: async (email: string, password: string) => {
    // Use URLSearchParams for application/x-www-form-urlencoded format
    // OAuth2PasswordRequestForm expects form data, not JSON
    const params = new URLSearchParams();
    params.append('username', email);
    params.append('password', password);

    const response = await api.post('/auth/login', params.toString(), {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });

    const { access_token } = response.data;
    console.log('=== Login Success ===');
    console.log('Token received (first 30 chars):', access_token?.substring(0, 30) + '...');
    localStorage.setItem('access_token', access_token);
    console.log('Token stored in localStorage:', !!localStorage.getItem('access_token'));
    set({ isAuthenticated: true, user: null });
  },

  register: async (email: string, password: string, fullName: string) => {
    console.log('=== Register Attempt ===');
    console.log('Email:', email);
    console.log('Full Name:', fullName);
    
    try {
      const response = await api.post('/auth/register', {
        email,
        password,
        full_name: fullName,
      });
      
      console.log('=== Register Success ===');
      console.log('Response:', response.data);
      
      // After registration, log in
      console.log('=== Auto-login after registration ===');
      await useAuthStore.getState().login(email, password);
    } catch (error: any) {
      console.error('=== Register Error ===');
      console.error('Error:', error);
      console.error('Response:', error.response?.data);
      console.error('Status:', error.response?.status);
      throw error; // Re-throw to let the Login component handle it
    }
  },

  logout: () => {
    localStorage.removeItem('access_token');
    set({ isAuthenticated: false, user: null, meLoading: false });
  },

  setAuthenticated: (token: string) => {
    localStorage.setItem('access_token', token);
    set({ isAuthenticated: true, user: null });
  },

  fetchMe: async () => {
    set({ meLoading: true });
    try {
      const res = await api.get('/auth/me');
      const me = res.data as UserMe;
      set({ user: me, meLoading: false });
      return me;
    } catch (e) {
      set({ meLoading: false });
      throw e;
    }
  },

  updateMe: async (payload) => {
    set({ meLoading: true });
    try {
      const res = await api.patch('/auth/me', payload);
      const me = res.data as UserMe;
      set({ user: me, meLoading: false });
      return me;
    } catch (e) {
      set({ meLoading: false });
      throw e;
    }
  },
}));
